import React, { useState, useEffect } from 'react'
import { FolderOpen, Trash2, Clock, Film, Download, Youtube, Loader2, Plus, HardDrive } from 'lucide-react'
import { projectApi } from '../api/client'

interface ProjectLibraryProps {
  onLoadProject: (projectId: string) => void
  onNewProject: () => void
  onClose: () => void
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return m > 0 ? `${m}m ${s}s` : `${s}s`
}

function timeAgo(dateStr: string): string {
  const d = new Date(dateStr)
  const now = new Date()
  const diff = now.getTime() - d.getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 30) return `${days}d ago`
  return d.toLocaleDateString()
}

type Project = {
  id: string
  title: string
  video_url: string
  video_duration: number
  thumbnail_url?: string
  video_source?: string
  segment_count: number
  export_count: number
  created_at: string
  updated_at: string
  last_opened_at: string
}

type Export = {
  export_id: string
  project_id?: string
  project_title?: string
  thumbnail_url?: string
  filename: string
  file_size?: number
  file_url?: string
  export_type: string
  destination_url?: string
  created_at: string
  expires_at?: string
}

type StorageUsage = {
  total_bytes: number
  project_count: number
  clip_count: number
  export_count: number
  storage_limit_bytes: number
  percent_used: number
}

export function ProjectLibrary({ onLoadProject, onNewProject, onClose }: ProjectLibraryProps) {
  const [tab, setTab] = useState<'projects' | 'exports'>('projects')
  const [projects, setProjects] = useState<Project[]>([])
  const [exports, setExports] = useState<Export[]>([])
  const [storage, setStorage] = useState<StorageUsage | null>(null)
  const [loading, setLoading] = useState(true)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const [projResult, expResult, storResult] = await Promise.all([
        projectApi.listProjects(),
        projectApi.listExports(),
        projectApi.getStorageUsage(),
      ])
      if (projResult.success) setProjects(projResult.projects)
      if (expResult.success) setExports(expResult.exports)
      if (storResult.success) setStorage(storResult)
    } catch (err) {
      console.error('Failed to load project library:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (projectId: string) => {
    if (!confirm('Delete this project? This cannot be undone.')) return
    setDeletingId(projectId)
    try {
      await projectApi.deleteProject(projectId)
      setProjects(prev => prev.filter(p => p.id !== projectId))
    } catch {
      // ignore
    } finally {
      setDeletingId(null)
    }
  }

  const exportTypeIcon = (type: string) => {
    if (type === 'youtube') return <Youtube size={14} className="text-red-400" />
    if (type === 'google_drive' || type === 'dropbox' || type === 'onedrive') return <FolderOpen size={14} className="text-blue-400" />
    return <Download size={14} className="text-green-400" />
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center" onClick={onClose}>
      <div
        className="bg-gray-800 rounded-xl w-full max-w-2xl shadow-2xl max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <FolderOpen size={22} className="text-indigo-400" />
            <h2 className="text-lg font-bold text-white">My Projects</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { onNewProject(); onClose() }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <Plus size={16} /> New Project
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-white text-xl px-2">&times;</button>
          </div>
        </div>

        {/* Storage bar */}
        {storage && (
          <div className="px-4 pt-3 pb-1">
            <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
              <span className="flex items-center gap-1">
                <HardDrive size={12} />
                {formatBytes(storage.total_bytes)} / {formatBytes(storage.storage_limit_bytes)}
              </span>
              <span>
                {storage.project_count} projects &middot; {storage.clip_count} clips &middot; {storage.export_count} exports
              </span>
            </div>
            <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  storage.percent_used > 90 ? 'bg-red-500' :
                  storage.percent_used > 70 ? 'bg-yellow-500' : 'bg-indigo-500'
                }`}
                style={{ width: `${Math.min(100, storage.percent_used)}%` }}
              />
            </div>
            {storage.percent_used > 80 && (
              <p className="text-xs text-yellow-400 mt-1">
                Running low on storage.{' '}
                <a href="/upgrade" className="underline hover:text-yellow-300">Upgrade for more</a>
              </p>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-gray-700 px-4">
          <button
            onClick={() => setTab('projects')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === 'projects' ? 'border-indigo-500 text-white' : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            Projects ({projects.length})
          </button>
          <button
            onClick={() => setTab('exports')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === 'exports' ? 'border-indigo-500 text-white' : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            Exports ({exports.length})
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-400">
              <Loader2 size={24} className="animate-spin mr-2" /> Loading...
            </div>
          ) : tab === 'projects' ? (
            projects.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Film size={48} className="mx-auto mb-3 opacity-50" />
                <p className="text-sm">No projects yet. Import a video to get started!</p>
              </div>
            ) : (
              <div className="space-y-2">
                {projects.map(p => (
                  <div
                    key={p.id}
                    className="flex items-center gap-3 p-3 bg-gray-700/50 rounded-lg hover:bg-gray-700 cursor-pointer transition-colors group"
                    onClick={() => { onLoadProject(p.id); onClose() }}
                  >
                    {/* Thumbnail */}
                    <div className="w-20 h-12 bg-gray-600 rounded flex-shrink-0 overflow-hidden">
                      {p.thumbnail_url ? (
                        <img src={p.thumbnail_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Film size={20} className="text-gray-500" />
                        </div>
                      )}
                    </div>
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-white truncate">{p.title}</h3>
                      <div className="flex items-center gap-3 text-xs text-gray-400 mt-0.5">
                        <span>{formatDuration(p.video_duration)}</span>
                        <span>{p.segment_count} clips</span>
                        {p.export_count > 0 && <span>{p.export_count} exports</span>}
                        <span className="flex items-center gap-1">
                          <Clock size={10} /> {timeAgo(p.last_opened_at || p.updated_at)}
                        </span>
                      </div>
                    </div>
                    {/* Actions */}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(p.id) }}
                      disabled={deletingId === p.id}
                      className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-red-400 p-1 transition-all"
                      title="Delete project"
                    >
                      {deletingId === p.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                    </button>
                  </div>
                ))}
              </div>
            )
          ) : (
            /* Exports tab */
            exports.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Download size={48} className="mx-auto mb-3 opacity-50" />
                <p className="text-sm">No exports yet. Export clips from a project!</p>
              </div>
            ) : (
              <div className="space-y-2">
                {exports.map(e => (
                  <div
                    key={e.export_id}
                    className="flex items-center gap-3 p-3 bg-gray-700/50 rounded-lg"
                  >
                    <div className="flex-shrink-0">
                      {exportTypeIcon(e.export_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{e.filename}</p>
                      <div className="flex items-center gap-3 text-xs text-gray-400 mt-0.5">
                        {e.project_title && <span>{e.project_title}</span>}
                        <span>{e.export_type}</span>
                        {e.file_size && <span>{formatBytes(e.file_size)}</span>}
                        <span>{timeAgo(e.created_at)}</span>
                        {e.expires_at && (
                          <span className="text-yellow-500">
                            expires {timeAgo(e.expires_at)}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Download link */}
                    {e.file_url && (
                      <a
                        href={e.file_url}
                        download={e.filename}
                        className="text-indigo-400 hover:text-indigo-300 p-1"
                        title="Re-download"
                        onClick={ev => ev.stopPropagation()}
                      >
                        <Download size={16} />
                      </a>
                    )}
                    {/* YouTube link */}
                    {e.destination_url && (
                      <a
                        href={e.destination_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-red-400 hover:text-red-300 p-1"
                        title="View on YouTube"
                        onClick={ev => ev.stopPropagation()}
                      >
                        <Youtube size={16} />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  )
}
